if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        const hasDots = !!this.querySelector('.slider-counter__link--dots');
        if (!this.elements.thumbnails && !hasDots) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

          // Flechas en escritorio: con un diseno de miniaturas la galeria no es un
          // contenedor de scroll (el overflow-x del <ul> solo existe por debajo de 750px),
          // asi que la flecha no puede desplazar nada. Lo que hace es cambiar la imagen
          // activa, exactamente igual que pulsar una miniatura.
          // Va en fase de CAPTURA sobre el <slider-component>: SliderComponent engancha
          // sus listeners en los propios botones, y solo la captura corre antes pase lo que
          // pase con el orden de upgrade de los custom elements. Sin esto, su onButtonClick
          // haria scrollTo({ left: NaN }).
          if (this.dataset.desktopLayout.includes('thumbnail')) {
            this.elements.viewer.addEventListener('click', this.onDesktopArrow.bind(this), true);
            this.syncDesktopArrows();
          }
        }

        // Dots clicables: el enganche de clic de Dawn (linkToSlide) vive en
        // SlideshowComponent, no en SliderComponent, asi que aqui eran solo
        // indicadores. El clic va por la misma ruta que una miniatura:
        // setActiveMedia ya scrollea el <ul> en movil. Listener normal, sin
        // captura: SliderComponent no engancha nada en los dots.
        this.querySelectorAll('.slider-counter__link--dots').forEach((dot, index) => {
          dot.addEventListener('click', this.onDotClick.bind(this, index));
        });
      }

      desktopSlides() {
        // .product__media-item y no [data-media-id] a secas: el boton de zoom de cada slide
        // lleva su propio data-media-id y colaria items duplicados.
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
      }

      syncDesktopArrows() {
        const slides = this.desktopSlides();
        if (slides.length < 2) return;
        const i = slides.findIndex((el) => el.classList.contains('is-active'));
        const prev = this.elements.viewer.querySelector('button[name="previous"]');
        const next = this.elements.viewer.querySelector('button[name="next"]');
        if (!prev || !next || !this.mql.matches) return;
        prev.toggleAttribute('disabled', i <= 0);
        next.toggleAttribute('disabled', i === slides.length - 1);
      }

      onDesktopArrow(event) {
        if (!this.mql.matches) return;
        const button = event.target.closest('button[name="previous"], button[name="next"]');
        if (!button || !this.elements.viewer.contains(button)) return;
        const slides = this.desktopSlides();
        if (slides.length < 2) return;
        event.stopPropagation();
        const current = slides.findIndex((el) => el.classList.contains('is-active'));
        const target = button.name === 'next' ? current + 1 : current - 1;
        if (target < 0 || target >= slides.length) return;
        this.setActiveMedia(slides[target].dataset.mediaId, false);
        this.syncDesktopArrows();
      }

      onDotClick(index) {
        // desktopSlides() vale tambien aqui: son los mismos .product__media-item
        // (ver el comentario del selector alli).
        const slides = this.desktopSlides();
        const slide = slides[index];
        if (!slide || slide.classList.contains('is-active')) return;
        this.setActiveMedia(slide.dataset.mediaId, false);
        this.syncDesktopArrows();
        // Feedback inmediato: el slideChanged del scroll llega debounced (500ms)
        // y en un layout sin scroll no llega. Idempotente con onSlideChanged.
        this.querySelectorAll('.slider-counter__link--dots').forEach((dot, i) => {
          dot.classList.toggle('slider-counter__link--active', i === index);
        });
      }

      onSlideChanged(event) {
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        const dots = this.querySelectorAll('.slider-counter__link--dots');
        dots.forEach((dot, i) => {
          dot.classList.toggle('slider-counter__link--active', i === event.detail.currentPage - 1);
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Batch reads before writes to avoid forced reflow
          const offsetLeft = activeMedia.offsetLeft;
          const activeMediaRect = activeMedia.getBoundingClientRect();

          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: offsetLeft });
          }
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
